import constants from '@Common/Core/Constant';
import { AggregationType, TimeInterval, TypeKind } from '@Common/Enums';
import { ViewLayoutOrientation } from '@Common/Enums/Enum';
import documentUtils from '@Common/Helpers/DocumentUtils';
import SeriesHelper from '@Common/Helpers/SeriesHelper';
import instanceFactory, {
    BaseDataDefinition,
    BaseDefinition,
    BaseSeries,
    DashboardTheme,
    DateTimeDuration,
    DocumentBase,
    DocumentDescriptor,
    DocumentReference,
    DocumentReferenceBase,
    DriverGroup,
    FilterPeerGroupDocument,
    GlobalDriverGroup,
    IIdentifiable,
    KPIScopeDefinition,
    KPISettingDefinition,
    Metric,
    MetricSeries,
    ProcessAttributeDefinition,
    ProcessAttributeSeries,
    ProcessDefinition,
    ProcessFilterDefinition,
    ProjectModel,
    RecordDefinition,
    RecordDocument,
    RecordFieldDefinition,
    RecordSeries,
    SwimlaneGroup,
    TitleColorList,
    TranslationTable,
    TTDefinition,
    TTItemDefinition,
    ViewComponent,
    ViewerModel,
    ViewLayout
} from '@InstanceFactory';

import Maybe from '../Core/Maybe';
import { documentCategories } from '@Common/Core/DocumentCategories';
import { Errors } from '@Common/Helpers/Errors';
import documentsFactory from '@Common/ApplicationCore/DocumentsFactory';

export interface Identifier {
    id?: string;
    name?: string;
}

namespace documentHelper {
    import getTypeByCategory = documentsFactory.getTypeByCategory;
    const documentReferenceTypes = constants.documentReferenceTypes;

    export function createDefinition(ref: DocumentReferenceBase): BaseDefinition {
        const category = documentUtils.getDocumentCategory(ref);
        switch (category) {
            case documentCategories.metric:
            case documentCategories.metricStandard:
            case documentCategories.metricGroup:
            case documentCategories.metricDerived:
            case documentCategories.metricProcess:
                return KPISettingDefinition.create({ id: ref.id, name: ref.name, category });
            case documentCategories.record:
                return RecordDefinition.create({ id: ref.id, name: ref.name, category });
            case documentCategories.tt:
                return TTDefinition.create({ id: ref.id, name: ref.name, category });
        }
        Errors.unsupported(`Category = ${category}`);
    }

    export function getProcess(projectOrProcessDef: string | ProcessDefinition[], processIdentifier: Identifier): ProcessDefinition {
        const processes = utils.getDefinitions(projectOrProcessDef, documentCategories.process);
        return utils.findDefinition(processes, processIdentifier);
    }

    export function getProcessAttribute(projectId: string, processIdentifier: Identifier, attributePath: string): ProcessAttributeDefinition {
        const process = getProcess(projectId, processIdentifier);
        return process && _.find(process.processAttributes, p => p.id === attributePath);
    }

    export function findRecord(
        projectOrRecords: string | RecordDefinition[],
        recordIdentifier: Identifier & { recordId?: string }
    ): RecordDefinition {
        if (!_.isNil(recordIdentifier) && recordIdentifier.recordId && _.isNil(recordIdentifier.id)) {
            recordIdentifier.id = recordIdentifier.recordId;
        }

        const records = utils.getDefinitions(projectOrRecords, documentCategories.record);
        return utils.findDefinition(records, recordIdentifier);
    }

    export function findRecordField(
        projectOrRecords: string | RecordDefinition[],
        recordIdentifier: Identifier & { recordId?: string },
        fieldIdentifier: Identifier
    ): { field: RecordFieldDefinition; record: RecordDefinition } {
        const record = findRecord(projectOrRecords, recordIdentifier);
        if (record) {
            const field = utils.findDefinition(record.fields, fieldIdentifier);
            if (field) {
                return { field, record };
            }
        }
        return null;
    }

    export function findRecordFieldBySeries(projectId: Guid, series: RecordSeries): RecordFieldDefinition | null {
        const record = findRecord(projectId, series.record);
        if (record) {
            return utils.findDefinition(record.fields, { id: series.fieldPath });
        }
        return null;
    }

    export function findDimension(projectId: string, metricIds: string[], dimensionId: string): KPIScopeDefinition {
        const metrics = getMetrics(projectId).searchTree();
        return (
            _(metricIds)
                .flatMap(mid => metrics[mid] && metrics[mid].dimensions)
                .compact()
                .filter(d => d.id === dimensionId)
                .first() || null
        );
    }

    export function getSelectedRecordAsArray(projectId, recordId) {
        const record = findRecord(projectId, { id: recordId });
        return record
            ? [
                  {
                      documentId: record.id,
                      documentName: record.name,
                      projectId: projectId
                  }
              ]
            : [];
    }

    export function getRecords(projectId: string): RecordDefinition[] {
        return getDocuments<RecordDefinition>(projectId, documentCategories.record);
    }

    export function getMetrics(projectId: string): KPISettingDefinition[] {
        return getDocuments<KPISettingDefinition>(projectId, documentCategories.metric);
    }

    export function metricDocumentToDefinition(document): KPISettingDefinition {
        return KPISettingDefinition.create({
            id: document.id,
            name: document.name,
            category: constants.documentCategories.metric,
            dimensions: _.map(document.items, (i: any) => KPIScopeDefinition.create({ id: i.id, name: i.displayName }))
        });
    }

    export function getMetricDisplayName_by_def(metric, record) {
        if (metric) {
            const metricRef = getReferenceByDefinition(metric);
            return getMetricDisplayName_by_ref(metricRef, record);
        }
        return undefined;
    }

    export function getMetricDisplayName_by_ref(metricRef, record: RecordDefinition) {
        if (metricRef) {
            if (metricRef.$type == documentReferenceTypes.autoMetric) {
                const field = record && _.find(record.fields, f => f.id === metricRef.fieldId);
                const aggregationType = AggregationType.getValue(metricRef.aggregation);

                const recordName = record ? record.name : metricRef.name;
                const fieldName = field ? field.name : metricRef.fieldId;
                return `${recordName}::${fieldName}::${aggregationType.name}`;
            } else if (metricRef.$type == documentReferenceTypes.metric || metricRef.$type == documentReferenceTypes.metricGroup) {
                return metricRef.name;
            }
        }
        return undefined;
    }

    export function getMetricReferenceById(metricId, projectId) {
        let metricRef = null;
        const newMetric = getMetric(projectId, { id: metricId });
        if (newMetric) {
            metricRef = getReferenceByDefinition(newMetric);
        }
        return metricRef;
    }

    export type Reference<T extends IIdentifiable> = DocumentReference<T> & {
        fieldId?: string;
        aggregation?: string;
    };

    export function categoryToReference(category: string): string {
        const key = _.findKey(documentCategories, v => v === category);
        const referenceType = documentReferenceTypes[key];
        if (referenceType) {
            return referenceType;
        }
        const typeByCategory = getTypeByCategory(category);
        return (
            'AltoSoft.Insight.Common.RepositoryInterfaces.DocumentReference`1[[AltoSoft.Insight.DataDefs.DocumentModels.' +
            instanceFactory[typeByCategory].instanceName +
            ', AltoSoft.Insight.DataDefs]], AltoSoft.Insight.Common'
        );
    }

    export function getReferenceByDefinition<T extends RecordDocument>(definition: RecordDefinition): Reference<RecordDocument>;
    export function getReferenceByDefinition<T extends TranslationTable>(definition: TTDefinition): Reference<TranslationTable>;
    export function getReferenceByDefinition<T extends Metric>(definition: KPISettingDefinition): Reference<Metric>;
    export function getReferenceByDefinition<T extends IIdentifiable>(definition: BaseDefinition): Reference<T>;
    export function getReferenceByDefinition(definition: BaseDefinition): Reference<IIdentifiable> {
        if (definition) {
            const refTypeName = categoryToReference(definition.category);
            if (refTypeName) {
                let reference: Reference<IIdentifiable> = {
                    $type: refTypeName,
                    id: definition.id,
                    name: definition.name,
                    isChanged: false,
                    document: null
                };
                if (refTypeName === documentReferenceTypes.autoMetric) {
                    const autoMetricDef = definition as any;
                    reference = _.assign(reference, {
                        fieldId: autoMetricDef.fieldId,
                        aggregation: autoMetricDef.aggregation
                    });
                }
                return reference;
            }
        }
        return undefined;
    }

    export function findMetric(metricReference, metrics: KPISettingDefinition[], records: RecordDefinition[]): KPISettingDefinition {
        if (metricReference) {
            if (metricReference.$type == documentReferenceTypes.autoMetric) {
                const record = _.find(records, utils.findById(metricReference.id));
                if (!record) {
                    throw 'Auto metric "{0}" not found'.format(metricReference.name);
                }
                const field = _.find(record.fields, utils.findById(metricReference.fieldId));

                const format = getDefaultFormatByRecordField(field);

                const duration = DateTimeDuration.create();
                duration.intervalType = TimeInterval.day1;
                const unSupportDimensionType = [TypeKind.text, TypeKind.binary, TypeKind.dateTime] as TypeKind[];
                const dimensions = record.fields.filter(f => !unSupportDimensionType.contains(f.type) && f.categoryField);

                return {
                    id: metricReference.id,
                    name: metricReference.name,
                    category: documentCategories.autoMetric,
                    aggregation: metricReference.aggregation,
                    fieldId: metricReference.fieldId,
                    defaultFormatName: format,
                    metricType: field.type,
                    calculationInterval: duration,
                    dimensions: dimensions
                } as any;
            } else {
                return utils.findDefinition(metrics, metricReference);
            }
        }
        return undefined;
    }

    export function getMetric(projectId, metricReference): KPISettingDefinition {
        const records = getRecords(projectId);
        const metrics = getMetrics(projectId);
        return findMetric(metricReference, metrics, records);
    }

    export function getTranslationTables(projectId: string): TTDefinition[] {
        return utils.getDefinitions<TTDefinition>(projectId, documentCategories.tt);
    }

    export function getTranslationTable(projectOrTTDefinitions: string | TTDefinition[], identifier: Identifier): TTDefinition | null {
        const tts = utils.getDefinitions(projectOrTTDefinitions, documentCategories.tt);
        return utils.findDefinition(tts, identifier);
    }

    export function findTTField(
        projectId: string,
        ttId: string,
        fieldName: string
    ): { translationTable: TTDefinition; field: TTItemDefinition } | null {
        const translationTable = getTranslationTable(projectId, { id: ttId });
        if (translationTable) {
            const field = utils.findDefinition(translationTable.ttFields, { id: fieldName });
            if (field) {
                return { translationTable, field };
            }
        }
        return null;
    }

    interface IDefaultRecord {
        recordDefinition: RecordDefinition;
        metricSeriesId: string;
    }

    export function getDefaultRecord(projectId: Guid, dataDefinition: BaseDataDefinition): IDefaultRecord {
        const metrics = getMetrics(projectId).searchTree();
        const records = getRecords(projectId);

        const usedMetricSeries = _.filter(dataDefinition.series as MetricSeries[], series => series.inUse && series.metric != null);

        let metricSeriesId = constants.guidEmpty;
        //TODO: reverse logic
        const recordDefinition = _(records)
            .filter(record => {
                for (const series of usedMetricSeries) {
                    if (series.metric.$type == documentReferenceTypes.autoMetric) {
                        if (series.metric.id == record.id) {
                            metricSeriesId = series.seriesId;
                            return true;
                        }
                    }
                    const metricDefinition = metrics[series.metric.id];
                    if (metricDefinition && _.indexOf(metricDefinition.recordIds, record.id) != -1) {
                        metricSeriesId = series.seriesId;
                        return true;
                    }
                }
            })
            .first();

        return {
            recordDefinition: recordDefinition,
            metricSeriesId: metricSeriesId
        };
    }

    export interface IRecordFieldItem {
        id: string;
        name: string;
        formatName: string;
        fieldType: TypeKind;
        rowsCount?: number;
    }

    export function isEmptyId(id: string): boolean {
        return _.isNil(id) || id === '' || id === constants.guidEmpty;
    }

    export function getRecordFieldItems(seriesList: RecordSeries[], projectId: string): IRecordFieldItem[] {
        return _(seriesList)
            .map(series => {
                const recordField = series.inUse && findRecordField(projectId, { id: series.record.id }, { id: series.fieldPath });
                const field = recordField && recordField.field;
                return (
                    field && {
                        id: recordField.field.id,
                        name: series.appearance.caption,
                        formatName: series.appearance.formatName,
                        fieldType: !isEmptyId(recordField.field.ttId) ? (TypeKind.string as TypeKind) : (recordField.field.type as TypeKind)
                    }
                );
            })
            .compact()
            .value();
    }

    export function hasProject(projectId: string): boolean {
        return _.some(altosoft.projectDocuments, utils.findById(projectId));
    }

    export function deleteProject(projectId: string): void {
        _.remove(altosoft.projectDocuments, utils.findById(projectId));
    }

    export function getProject(projectId: string, canBeNull: boolean = true): ProjectModel {
        const project = _.find(altosoft.projectDocuments, utils.findById(projectId));
        if (project) {
            return project;
        }
        return canBeNull ? null : utils.emptyProject;
    }

    export function getDocuments(projectId: string, category: typeof documentCategories.metric): KPISettingDefinition[];
    export function getDocuments(projectId: string, category: typeof documentCategories.record): RecordDefinition[];
    export function getDocuments<T extends BaseDefinition>(projectId: string, category: string): T[];
    export function getDocuments<T extends DocumentBase>(projectId: string, category: string): T[];
    export function getDocuments(projectId: string, category: string): any[] {
        const project = getProject(projectId, false);
        switch (category) {
            case documentCategories.metric:
                return project.metrics;

            case documentCategories.record:
                return project.records;

            case documentCategories.tt:
                return project.translationTables;

            case documentCategories.process:
                return project.processes;

            case documentCategories.alert:
                return project.alerts;

            case documentCategories.customFilterGroup:
                return project.customFilterGroups;

            case documentCategories.filterGroup:
                return project.driverGroups;

            case documentCategories.report:
                return project['reports']; //????????

            case documentCategories.parameter:
                return project.parameters;

            case documentCategories.swimlaneGroup:
                return project.swimlaneGroups;

            case documentCategories.processFilterDefinition:
                return project.processFilterDefinitions;
        }
    }

    export function getTitleColors(projectId: string): TitleColorList {
        const project = getProject(projectId, false);
        return project.colorsList;
    }

    export function getLocalDriverGroups(projectId: string): DriverGroup[] {
        const documents = getDocuments<DriverGroup>(projectId, constants.documentCategories.filterGroup);
        return documents ? documents : [];
    }

    export function getGlobalDriverGroups(): GlobalDriverGroup[] {
        const model = altosoft.model as ViewerModel;
        return Maybe(model)
            .read(m => m.globalDriverGroups)
            .valueOrDefault([]);
    }

    export function getAllDriverGroups(projectId: string): (DriverGroup | GlobalDriverGroup)[] {
        const localDriverGroups = getLocalDriverGroups(projectId).slice();
        return [...localDriverGroups, ...getGlobalDriverGroups()];
    }

    export function getCustomFilterGroups(projectId: string, dimensionName: string): FilterPeerGroupDocument[] {
        const documents = (getDocuments(projectId, documentCategories.customFilterGroup) as any) as FilterPeerGroupDocument[];
        return _.filter(documents, group => group.dimensionName === dimensionName);
    }

    export function getSwimlaneGroups(projectId: string): SwimlaneGroup[] {
        const project = getProject(projectId);
        return _.clone(project.swimlaneGroups);
    }

    export function getProcessFilterDefinitions(projectId: string): ProcessFilterDefinition[] {
        const project = getProject(projectId);
        return _.clone(project.processFilterDefinitions);
    }

    export function getDocumentById<T extends DocumentBase>(projectId: string, category: string, documentId: string): T;
    export function getDocumentById<T extends BaseDefinition = BaseDefinition>(projectId: string, documentType: string, documentId: string): T;
    export function getDocumentById(projectId: string, documentType: string, documentId: string): any {
        const docs = getDocuments(projectId, documentType);
        return _.find(docs, utils.findById(documentId)) as any;
    }

    export function getSeriesType(projectId: string, series: BaseSeries, processRef: Identifier): string {
        if (ProcessAttributeSeries.isInstanceOfType(series)) {
            const processAttribute = getProcessAttribute(projectId, processRef, series.attributePath);
            return processAttribute && processAttribute.type;
        }
        const seriesType = new SeriesHelper(projectId).getSeriesType(series);
        if (seriesType === null) {
            throw new Error('Unknown series type');
        }
        return seriesType;
    }

    export function getSupportedLocales(projectId: string): string[] {
        const project = getProject(projectId);
        return project.supportedLocales;
    }

    export function getDefaultFormat(projectId: string, series: MetricSeries | RecordSeries): string {
        const metricSeries = series as MetricSeries;
        const recordSeries = series as RecordSeries;
        if (recordSeries.record) {
            const field = findRecordField(projectId, recordSeries.record, { id: recordSeries.fieldPath });
            if (field) {
                return getDefaultFormatByRecordField(field.field);
            }
        }
        if (metricSeries.metric) {
            const metric = getMetric(projectId, metricSeries.metric);
            if (metric) {
                return metric.defaultFormatName;
            }
        }
        return constants.numberFormatName;
    }

    export function getDefaultFormatByRecordField(field: RecordFieldDefinition | TTItemDefinition): string {
        const recordField = field as RecordFieldDefinition;
        if (recordField.formatName) {
            return recordField.formatName;
        }

        switch (field.type) {
            case TypeKind.dateTime:
                return constants.defaultDateFormatName;
            case TypeKind.int:
                return constants.numberNoDecimalFormatName;
            default:
                return constants.numberFormatName;
        }
    }

    export function getMetricLinkedProcessIds(metrics: KPISettingDefinition[]): string[] {
        return _(metrics)
            .flatMap(metric => metric.dimensions || [])
            .flatMap(dimension => dimension.processProperties || [])
            .filter(prop => !isEmptyId(prop.id))
            .map(prop => prop.id)
            .uniq()
            .value();
    }

    export const defaultLayout: ViewLayout = ViewLayout.create({
        id: constants.guidEmpty,
        name: strings.viewLayout.default,
        orientation: ViewLayoutOrientation.allOrientation
    });

    /**
     * If there is several layouts defined for view,
     * find and apply most appropriate according with passed width and height
     *
     * @param {number} w - viewer width
     * @param {number} h - viewer height
     */
    export function setActiveLayout(view: ViewComponent, w: number, h: number): void {
        const definedLayouts = view.appearances
            .map(app => {
                return _.find(altosoft.model.insightMeta.viewLayouts as ViewLayout[], layout => {
                    return layout.id === app.viewLayout.id;
                });
            })
            .filter(app => !!app);

        const selectedLayout = _(definedLayouts)
            .sortBy(
                d => d.width,
                d => d.height
            )
            .find(layout => {
                const fitSize = layout.width >= w && layout.height >= h;
                const fitOrientation =
                    w / h >= 1
                        ? layout.orientation !== ViewLayoutOrientation.portraitOnly
                        : layout.orientation !== ViewLayoutOrientation.landscapeOnly;

                return fitSize && fitOrientation;
            });
        view.activeLayout = documentUtils.createInsightReference(selectedLayout ? selectedLayout : defaultLayout);
    }

    /**
     * Returns list of all available viewLayouts,  with 'Default' item at head
     */
    export function getViewLayoutList(): ViewLayout[] {
        return _.cloneDeep([defaultLayout, ...altosoft.model.insightMeta.viewLayouts]);
    }

    function getProjectModel(projectId: Guid): ProjectModel;
    function getProjectModel(projectId: Guid, throwable: false): ProjectModel | null;
    function getProjectModel(projectId: Guid, throwable: boolean = true): ProjectModel {
        const projectModel = _.find(altosoft.projectDocuments, utils.findById(projectId));
        if (!projectModel && throwable) {
            throw new Error(`Project model for projectId : ${projectId} is not loaded`);
        }
        return projectModel;
    }

    export function getDataSources(projectId: Guid): DocumentDescriptor[] {
        const projectModel = getProjectModel(projectId);
        return _.cloneDeep(projectModel.dataSources);
    }

    /**
     * Return list of all  available viewLayouts(with 'Default') with  overridden themeId from project settings
     *
     * @export
     * @param {Guid} projectId - project id
     * @returns {ViewLayout[]}
     */
    export function getProjectsViewLayouts(projectId: Guid): ViewLayout[] {
        const projectModel = getProjectModel(projectId);
        const themes = projectModel.configurationSettings.projectThemes ?? {};
        return _.map(getViewLayoutList(), layout => {
            const themeId = themes[layout.id] ?? themes[defaultLayout.id] ?? layout.theme?.id;
            const ref = themeId
                ? documentUtils.createInsightReference({
                    $type: DashboardTheme.typeName,
                    id: themeId,
                    name: altosoft.model.user.allowedThemes[themeId]
                } as DashboardTheme)
                : undefined;
            _.assign(layout, { theme: ref });
            return layout;
        });
    }

    export namespace utils {
        export function findDefinition<T extends BaseDefinition>(definitions: T[], what: Identifier): T | null {
            let res: T = null;
            if (_.isNil(what)) {
                return res;
            }
            if (!_.isNil(what.id)) {
                res = _.find(definitions, findById(what.id));
            }
            if (_.isNil(res) && !_.isNil(what.name)) {
                res = _.find(definitions, def => def.name === what.name);
            }
            return res;
        }

        export function getDefinitions<T extends BaseDefinition>(projectIdOrDefinitions: string | T[], docType: string): T[] {
            return typeof projectIdOrDefinitions === 'string' ? getDocuments<T>(projectIdOrDefinitions, docType) : projectIdOrDefinitions;
        }

        export function findById<T extends { id: string }>(id: string): (item: T) => boolean {
            return item => item.id === id;
        }

        export const emptyProject: ProjectModel = {
            metrics: [],
            records: [],
            translationTables: [],
            processes: [],
            alerts: [],
            customFilterGroups: [],
            driverGroups: [],
            reports: [], //????:[]?
            parameters: [],
            swimlaneGroups: [],
            processFilterDefinitions: [],
            colorsList: null
        } as any;
    }
}

export default documentHelper;
